// Tanner Fry
// dev.lasutriv@gmail.com
// Component used for creating a trask for the task tracker.

import '../../css/components/Animations.css';

import { ErrorMessage, Field, Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { APIPOSTUPDATEUserAnalyticsLoginTrack } from '../../../api/APIUserAnalytics';
import { ApiPostOtpCode, ReturnPropsAPIGetUserLoginResponse, VerifyOTPCodeData } from '../../../api/APIUsers';
import { useAppDispatch, useAppSelector } from '../../../AppHooks';
import { _settings_non_private_routes } from '../../../AppSettings';
import { setLoggedIn, setSubscriptionStatus, setUser } from '../../../slices/UserSlice';
import { SubscriptionPlan } from '../data/SubscriptionData';
import Loading from '../elements/Loading';

interface ILoginVerifyOTPFormProps {
    userPlan: SubscriptionPlan;
    apiLoginData: ReturnPropsAPIGetUserLoginResponse;
    // setUserPlan: React.Dispatch<React.SetStateAction<UserPlan>>
}

function LoginVerifyOTPForm(props: ILoginVerifyOTPFormProps) {  
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const apiLoginData = props.apiLoginData;
    const [errorMessage, setErrorMessage] = useState('');
    const [isCheckingFormErrorPlacement, setIsCheckingFormErrorPlacement] = useState(false);
    const [isAnimatingLogin, setIsAnimatingLogin] = useState(false);
    const [isAnimatingLoginComplete, setIsAnimatingLoginComplete] = useState(false);
    const lastLocationWhileLoggedIn = useAppSelector(state => state.users.lastLocation);
    const loginAnimationVariants = {
        swipe: {
            opacity: 1,
            top: "250%",
            transition: {
                duration: 1,
                ease: "easeInOut",
            },
        },
        end: {
            opacity: 1,
            top: "250%"
        },
    }

    useEffect(() => {
        setIsCheckingFormErrorPlacement(true);        
        // OTP is disabled - always bypass OTP verification and go to dashboard
        dispatch(setSubscriptionStatus(apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free));
        dispatch(setLoggedIn(true));
        
        // Navigate to dashboard or last location
        if (lastLocationWhileLoggedIn === "" || lastLocationWhileLoggedIn === '/login' || lastLocationWhileLoggedIn === '/register') {
            navigate("/dashboard");
        } else if (!_settings_non_private_routes.includes(lastLocationWhileLoggedIn)) {
            navigate(lastLocationWhileLoggedIn);
        } else {
            navigate("/dashboard");
        }
    }, [apiLoginData, dispatch, navigate, lastLocationWhileLoggedIn])

    useEffect(() => {
        // Get form error element and change position
        let formErrorElements = document.getElementsByClassName('form-error');
        if (formErrorElements.length > 0) {
            // Loop
            for (let i = 0; i < formErrorElements.length; i++) {
                // Get form error element's width
                let formErrorElementWidth = formErrorElements[i].clientWidth;
                // Set form error element's left position
                let formErrorStyle = 'left: -' + (10 + formErrorElementWidth) + 'px;';
                // Set form error element's style
                formErrorElements[i].setAttribute('style', formErrorStyle);
            }
            // console.log("formErrorElements: ", formErrorElements);
            
        }

        setIsCheckingFormErrorPlacement(false);
    }, [isCheckingFormErrorPlacement])

    useEffect(() => {
        // If done animating, perform actions after login such as routing user to dashboard or last location
        if (isAnimatingLoginComplete) {
            // Make sure we logged in successfully via APIGetUserLoginResponse
            if ((typeof JSON.parse(localStorage.getItem('user'))?.uuid) == 'string') {
                // TODO: Verify user credentials match server. If not, remove the local credentials
                dispatch(setLoggedIn(true));
            } else {
                dispatch(setLoggedIn(false));
                localStorage.removeItem('user');
                // Check if current route is in _settings_non_private_routes. If not, redirect to login
                if (!_settings_non_private_routes.includes(window.location.pathname)) {
                    navigate('/login');
                }
            }
            
            if (lastLocationWhileLoggedIn === "" && apiLoginData.error === "") {
                // Default navigate to dashboard if no last location is set and no errors
                // console.log("Navigate to /dashboard");
                navigate("/dashboard");
            } else {
                // console.log("Navigate to lastLocationWhileLoggedIn: ", lastLocationWhileLoggedIn);
                // Navigate to user's last location if no errors and they weren't just on the login/register page
                if (lastLocationWhileLoggedIn !== '/login' && lastLocationWhileLoggedIn !== '/register'
                    && apiLoginData.error === "") {
                    navigate(lastLocationWhileLoggedIn);
                } 
            } 
            // console.log("apiLoginData.error: ", apiLoginData.error);

            // Set user plan        
            // props.setUserPlan({...props.userPlan, plan: apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free} as UserPlan);
            dispatch(setSubscriptionStatus(apiLoginData.subscriberPlan ? apiLoginData.subscriberPlan : SubscriptionPlan.Free));
        }
    }, [isAnimatingLoginComplete]);

    async function submitForm(values, setSubmitting) {
        // Submit results to db
        const apiOTPData = await ApiPostOtpCode(values.otpCode);
        console.log("apiLoginData: ", apiLoginData);
        
        console.log("apiOTPData: ", apiOTPData);
        if (apiOTPData !== undefined && apiOTPData.error === "") {

            if (apiOTPData.data === VerifyOTPCodeData.APPROVED) {
                // POST analytics login data to db
                console.log("Posting to analytics");
                        
                // const apiPOSTLoginAnalytics = async () => {
                //     const response = await APIPOSTUPDATEUserAnalyticsLoginTrack();
                //     console.log("APIPOSTUPDATEUserAnalyticsLoginTrack response: ", response);
                // }
                // await apiPOSTLoginAnalytics();

                // Start animation for logging in
                setIsAnimatingLogin(true);        
                setSubmitting(false);
            } else {
                setErrorMessage("OTP Error: " + apiOTPData.data);
                setSubmitting(false);
            }
        } else {
            if (apiOTPData !== undefined) {
                setErrorMessage("Server returned nothing.");
            } else {
                setErrorMessage(apiLoginData.error);
                setSubmitting(false);
            }
        }
    }

    function resetForm() {
        
    }

    return (
        <>
            {/* <img className="login-splash-screen" src={require('../../files/splashScreens/Splash-Screen-Farmland.png')} alt="Farmland" /> */}
            <motion.div
                className='form-container-login-register'
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
            >
                <Formik
                    initialValues={ { otpCode: '' } }
                    validate={ values => {
                        setIsCheckingFormErrorPlacement(true);
                        const errors = {
                            otpCode: '',
                        };
                        
                        if (!values.otpCode) {
                            errors.otpCode = 'Required';
                        } else if (values.otpCode.length !== 6) {
                            errors.otpCode = 'Invalid OTP length. Must be 6 characters.';
                        }

                        if (values.otpCode.length === 6) {
                            return {};
                        } else {
                            return errors;
                        }
                    }}
                    onSubmit={ (values, { setSubmitting }) => {
                        submitForm(values, setSubmitting);
                    }}
                    onReset={ (values) => {
                        resetForm();
                    }}
                    enableReinitialize={true}
                >
                    { ({ isSubmitting }) => (
                        <>
                            { isSubmitting ? (
                                <>
                                    <Loading withBackground={true} />
                                </>
                            ) : (
                                <>
                                    {/* OTP is disabled - form is hidden */}
                                    <div style={{ textAlign: 'center', padding: '2rem' }}>
                                        <p>OTP verification is currently disabled. Redirecting...</p>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Formik>
            </motion.div>
        </>
    );
}


export default LoginVerifyOTPForm;